import type { ReactNode } from 'react';
import { CalendarBlankIcon, MapPinIcon, UserIcon } from '@phosphor-icons/react';
import type {
  ChatUiMobilityPlannerField,
  ChatUiMobilityPlannerPlace,
} from '@voll-intelligence/genui-contract';

/** A largura de uma coluna da fileira: as pontas dividem o que sobra, o Quando
 *  mede 180; na coluna estreita (celular) as três ocupam a largura toda. */
export function columnWidth(wide: boolean): string {
  return wide
    ? 'min-w-0 flex-1 @max-[theme(--breakpoint-sm)]:flex-none'
    : 'w-45 flex-none @max-[theme(--breakpoint-sm)]:w-full';
}

/** A fileira dos campos: lado a lado, alinhados pela base; empilhada no celular. */
export const ROW =
  'flex w-full min-w-0 items-end gap-2 @max-[theme(--breakpoint-sm)]:flex-col @max-[theme(--breakpoint-sm)]:items-stretch';

/** O rótulo da pergunta (`VOLL/caption-strong`), em cima do campo — no grid dos
 *  campos, sem recuo próprio (o «Quem» incluído). */
export const LABEL = 'truncate text-caption font-medium text-muted-foreground';

/** A pílula que não abre nada: o Quem e o Quando (sempre) e, no registro, todas. */
const STATIC_PILL =
  'flex h-9 min-w-0 items-center gap-2 rounded-lg bg-sand-3 px-3 text-ui text-foreground';
const STATIC_GLYPH = 'size-3.5 shrink-0 text-muted-foreground';

/** Uma coluna da fileira, como a barra da bancada: o rótulo da pergunta em
 *  cima (`aria-hidden`: o nome acessível sai do próprio campo), o campo
 *  embaixo. Sem `label` visível (a linha da Volta), só o campo. */
export function PlannerColumn({
  label,
  wide,
  className = '',
  children,
}: Readonly<{
  label?: string;
  wide: boolean;
  className?: string;
  children: ReactNode;
}>): ReactNode {
  return (
    <div className={`grid gap-1 ${columnWidth(wide)} ${className}`}>
      {label && (
        <span aria-hidden="true" className={LABEL}>
          {label}
        </span>
      )}
      <div className="relative min-w-0">{children}</div>
    </div>
  );
}

/** A pílula `sand-3` de só leitura. Na fileira viva o rótulo da coluna é
 *  `aria-hidden`, então a pergunta entra aqui para o leitor de tela (`srLabel`);
 *  sem valor, o texto de espera em `muted`. */
export function StaticPill({
  icon,
  text,
  emptyText,
  srLabel,
}: Readonly<{
  icon?: 'when' | 'place' | 'who';
  text: string | undefined;
  emptyText?: string;
  srLabel?: string;
}>): ReactNode {
  const Glyph =
    icon === 'when'
      ? CalendarBlankIcon
      : icon === 'place'
        ? MapPinIcon
        : icon === 'who'
          ? UserIcon
          : null;
  return (
    <span className={STATIC_PILL}>
      {Glyph && <Glyph className={STATIC_GLYPH} aria-hidden="true" />}
      <span className={`truncate${text ? '' : ' font-normal text-muted-foreground'}`}>
        {srLabel ? <span className="sr-only">{srLabel}: </span> : null}
        {text ?? emptyText}
      </span>
    </span>
  );
}

/** O lugar no registro: o escolhido, ou o termo que a pessoa disse quando nenhum
 *  foi escolhido aqui (o card remontado do histórico não sabe da escolha). */
export function recordPlaceText(
  value: ChatUiMobilityPlannerPlace | undefined,
  field: ChatUiMobilityPlannerField | undefined,
): string | undefined {
  return value?.title ?? field?.term;
}
